import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Shared popup used by the Dashboard top-metric cards. Renders a header,
 * scrollable list of items, and a single "Go to <page>" CTA.
 *
 * Pass `items` as an array of { id, primary, secondary?, badge? }.
 * Optional `headerExtra` lets callers slot a summary line above the list.
 */
export function MetricListPopup({
  open,
  onClose,
  title,
  description,
  Icon,
  items = [],
  emptyMessage = 'Nothing to show.',
  linkLabel,
  linkPath,
  headerExtra,
  testId,
}) {
  const navigate = useNavigate();
  const go = () => { onClose(); navigate(linkPath); };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-lg font-heading flex items-center gap-2">
            {Icon ? <Icon className="h-4 w-4" /> : null} {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-3 pb-3">
            {headerExtra}
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">{emptyMessage}</p>
            ) : (
              <div className="space-y-1.5">
                {items.map((it) => (
                  <div
                    key={it.id}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border hover:bg-muted/30 transition-colors"
                    data-testid={`${testId}-item-${it.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{it.primary}</p>
                      {it.secondary && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{it.secondary}</p>
                      )}
                    </div>
                    {it.badge && (
                      <span className="text-xs font-medium tabular-nums text-gray-700 whitespace-nowrap">
                        {it.badge}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        <Separator />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={go} className="gap-1.5" data-testid={`${testId}-go`}>
            <ExternalLink className="h-3.5 w-3.5" /> {linkLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
